import React, { useState, useEffect, useRef, useMemo } from 'react';
import Pagination from '../ui/Pagination';
import { useSearchParams } from "react-router-dom";
import { FaSearch, FaSort, FaAndroid, FaSitemap } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faArrowRightRotate } from '@fortawesome/free-solid-svg-icons';

import { useSite } from './../../context/SiteContext';
import LoadingSpinner from './../../utils/LoadingSpinner';
import userTransferService from './../../services/UserTransferService';
import { useUserList } from './../../hooks/useUserList';
import Button from './../ui/Button';
import userService from './../../services/UserService';
import TransferRequestModal from './../modals/TransferRequestModal';
import { getRequesterHighlightStyle } from './../../utils/requesterHighlight';

import './../../styles/Reports.css';
import './../../styles/DeviceModal.css';
import './../../styles/messageContainer.css';
import './../../styles/UserTransfer.css';
import InformationSection from './../InformationSection';

const PAGE_SIZE = 100;

const TransfersUsersTable = () => {
  const [searchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") === "transfers"
    ? "transfers"
    : "devices";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [deviceUsers, setDeviceUsers] = useState([]);
  const [transfers, setTransfers] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [visorSearch, setVisorSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'groupName', direction: 'asc' });
  const [error, setError] = useState(null);
  const [canFetchUsers, setCanFetchUsers] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const { selectedSiteId } = useSite();
  const dropdownRef = useRef(null);
  const toastTimerRef = useRef(null);

  const {
    users: userList,
    loading: loadingList,
    error: errorList,
  } = useUserList(searchQuery, selectedSiteId, { enabled: canFetchUsers, delay: 300 });

  const [transferModal, setTransferModal] = useState({
    open: false,
    user: null,
  });

  const isTransfersView = activeTab === 'transfers';

  const openTransferModal = (user) => setTransferModal({ open: true, user });
  const closeTransferModal = () => setTransferModal({ open: false, user: null });
  const showToast = (message) => {
    setToastMessage(message);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setToastMessage('');
      toastTimerRef.current = null;
    }, 2600);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "transfers" || tab === "devices") {
      setActiveTab(tab);
    }
  }, [searchParams]);
  const fetchDeviceUsers = async (showAll = false) => {
    if (!selectedSiteId) return;

    setIsLoading(true);
    setError(null);
    setActiveTab('devices');
    setCurrentPage(1);

    try {
      const response = await userService.getUsers(
        showAll ? '' : searchQuery,
        selectedSiteId
      );
      const data = Array.isArray(response) ? response : [];

      setDeviceUsers(data);

      if (data.length > 0) {
        const totalRecords = data[0].totalDevices || data.length;
        const calculatedPages = Math.ceil(totalRecords / PAGE_SIZE);
        setTotalPages(calculatedPages || 1);
      } else {
        setTotalPages(1);
        setError('No devices were found for this site.');
      }
    } catch {
      setError(
        'No records found.'
      );
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }  };

  const fetchTransfers = async () => {
    if (!selectedSiteId) {
      setError('Please select a site first.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setActiveTab('transfers');

    try {
      const response = await userTransferService.getAllTransfers(selectedSiteId);

      let data = [];
      if (Array.isArray(response)) {
        data = response;
      } else if (Array.isArray(response?.data)) {
        data = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        data = response.data.data;
      }

      setTransfers(data);

      if (data.length === 0) {
        setError('No pending transfer requests were found for this site.');
      }
    } catch {
      setError(
        'No pending transfer requests were found for this site. If you believe there should be some, please refresh the page or sign in again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'devices') return;
    if (searchQuery.length >= 3 && canFetchUsers) {
      const delay = setTimeout(() => {
        fetchDeviceUsers(false);
      }, 500);

      return () => clearTimeout(delay);
    }
  }, [searchQuery, canFetchUsers, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCanFetchUsers(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isTransfersView && currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage, isTransfersView]);

  const handleSelectUser = (user) => {
    setSearchQuery(user.username);
    setCanFetchUsers(false);
    setCurrentPage(1);
  };

  const handleSort = (columnKey) => {
    const isSameColumn = sortConfig.key === columnKey;
    const direction =
      isSameColumn && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: columnKey, direction });
  };

  const renderSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return <FaSort className="reports-font-style-grey" />;
  };

  const handleApplyFilters = () => {
    setCanFetchUsers(false);
    setCurrentPage(1);
    fetchDeviceUsers(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1) newPage = 1;
    if (newPage > totalPages) newPage = totalPages;
    setCurrentPage(newPage);
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    return [...data].sort((a, b) => {
      const valA = (a[key] ?? '').toString().toLowerCase();
      const valB = (b[key] ?? '').toString().toLowerCase();

      if (valA < valB) return -1 * dir;
      if (valA > valB) return 1 * dir;
      return 0;
    });
  };

  const sortedDeviceUsers = sortData(deviceUsers);

  const filteredTransfers = useMemo(() => {
  const q = visorSearch.trim().toLowerCase();
  if (!q) return transfers;

  return transfers.filter(t =>
    (t.visorId ?? '').toString().toLowerCase().includes(q)
  );
}, [transfers, visorSearch]);

const sortedTransfers = useMemo(() => {
  return sortData(filteredTransfers);
}, [filteredTransfers, sortConfig]);

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedDeviceUsers = sortedDeviceUsers.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  const handleSubmitTransfer = async (details) => {
    if (!transferModal.user) return;
    try {
      const payload = {
        visorRef: transferModal.user.visorId,
        groupId: transferModal.user.groupId,
        description: details,
      };
      await userTransferService.createUserTransfer(payload);
      showToast('Transfer request submitted successfully.');
      closeTransferModal();

      setDeviceUsers((prev) =>
        prev.map((u) =>
          u.visorId === transferModal.user.visorId
            ? { ...u, isPendingTransfer: true }
            : u
        )
      );
    } catch {
      alert('Failed to submit transfer request. Please try again.');
    }
  };

  const handleCancelTransfer = async (item, fromTransfersTab) => {
    try {
      await userTransferService.updateTransferRequest(item.visorId, 'canceled');
      alert('Transfer request cancelled (if one was active).');

      if (fromTransfersTab) {
        setTransfers((prev) => prev.filter((t) => t.id !== item.id));
      } else {
        setDeviceUsers((prev) =>
          prev.map((u) =>
            u.visorId === item.visorId ? { ...u, isPendingTransfer: false } : u
          )
        );
      }
    } catch {
      alert('Failed to cancel transfer request. Please try again.');
    }
  };

  return (
    <div className="user-transfer-report-container">
      {toastMessage && (
        <div className="transfer-toast-message" role="status" aria-live="polite">
          {toastMessage}
        </div>
      )}

      <div className="user-transfer-tab-bar">

        <button
          type="button"
          className={activeTab === 'devices' ? 'active' : ''}
          onClick={() => {
            setActiveTab('devices');
            setError(null);
            setVisorSearch('');
          }}
        >
          Devices
        </button>
        <button
          type="button"
          className={activeTab === 'transfers' ? 'active' : ''}
          onClick={() => {
            setActiveTab('transfers');
            setError(null);
            setVisorSearch('');
            if (transfers.length === 0) {
              fetchTransfers();
            }
          }}
        >
          Pending transfers
        </button>
      </div>

        <div className="user-transfer-report-main spacer-10">
          {activeTab === 'devices' && (
            
            <div className="user-transfer-report-search-container">
            <InformationSection filePath="md/transfers.md" />
            <div className="report-advanced-search">
            
              <div className="report-search-group">
                <div className="report-search-input-group">
                  <span className="report-search-input-group-text">
                    <FaSearch />
                  </span>
                  <div
                    ref={dropdownRef}
                    style={{ position: 'relative', display: 'inline-block' }}
                  >
                    <input
                      type="text"
                      placeholder="Search users"
                      value={searchQuery}
                      onChange={(e) => {
                        setCanFetchUsers(true);
                        setSearchQuery(e.target.value);
                      }}
                      className="report-search-form-control"
                      autoComplete="off"
                    />

                    {canFetchUsers && searchQuery.length >= 3 && (
                      <ul className="user-dropdown">
                        {loadingList && <li className="loading">Loading…</li>}
                        {errorList && (
                          <li className="error">Error: {errorList.message}</li>
                        )}
                        {!loadingList && !errorList && userList.length === 0 && (
                          <li className="no-results">No users found</li>
                        )}
                        {!loadingList &&
                          !errorList &&
                          userList.map((u) => (
                            <li key={u.id} onClick={() => handleSelectUser(u)}>
                              {u.username}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                </div>

                <Button onClick={handleApplyFilters} buttonStyle="btn-main">
                  Search
                </Button>

                <Button
                  onClick={() => fetchDeviceUsers(true)}
                  buttonStyle="btn-alternate"
                >
                  Show All Devices
                </Button>
              </div>
              </div>
            </div>
          )}
          
          {activeTab === 'transfers' && (
            <div className="user-transfer-report-search-container">
              <div className="report-advanced-search">
                <div className="report-search-group">
                  <div className="report-search-input-group">
                    <span className="report-search-input-group-text">
                      <FaSearch />
                    </span>
                    <div className="visor-search-wrapper">
                      <input
                        type="text"
                        value={visorSearch}
                        onChange={(e) => setVisorSearch(e.target.value)}
                        placeholder="Search visor ID…"
                        className="report-search-form-control visor-search-input"
                        autoComplete="off"
                      />

                      {visorSearch && (
                        <button
                          type="button"
                          className="visor-search-clear"
                          onClick={() => setVisorSearch('')}
                          aria-label="Clear visor search"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div style={{ marginLeft: '0.5rem' }}>
                      <Button onClick={fetchTransfers} buttonStyle="btn-main">
                      <FontAwesomeIcon icon={faArrowRightRotate}> </FontAwesomeIcon>{' '}
                        Refresh pending transfers
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="user-transfer-user-table-custom-container pagination-container">
            <div className="report-table-section">
              {isLoading && <LoadingSpinner />}

              {!isLoading && error ? (
                <div className="message-container">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    size="3x"
                    className="message-icon"
                  />
                  <p className="message-text">
                    {error || 'There are no records available at this time.'}
                  </p>
                </div>
              ) : (
                !isLoading && (
                  <div className="report-table-container">
                    <table className="report-table">
                      <thead>
                        {activeTab === 'devices' && (
                          <tr>
                            <th onClick={() => handleSort('groupName')}>
                              Group {renderSortArrow('groupName')}
                            </th>
                            <th onClick={() => handleSort('visorId')}>
                              Visor Reference {renderSortArrow('visorId')}
                            </th>
                            <th>Transfer actions</th>
                          </tr>
                        )}
                        {activeTab === 'transfers' && (
                          <tr>
                            <th onClick={() => handleSort('groupName')}>
                              Group {renderSortArrow('groupName')}
                            </th>
                            <th onClick={() => handleSort('visorId')}>
                              Visor Reference {renderSortArrow('visorId')}
                            </th>
                            <th onClick={() => handleSort('requester')}>
                              Requester {renderSortArrow('requester')}
                            </th>
                            <th>Description</th>
                            <th onClick={() => handleSort('requestDate')}>
                              Request Date {renderSortArrow('requestDate')}
                            </th>
                            <th>Transfer actions</th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {activeTab === 'devices' &&
                          pagedDeviceUsers.map((item, idx) => (
                            <tr key={idx}>
                              <td>
                                <FaSitemap className="reports-font-style-grey" />{' '}
                                {item.groupName || '—'}
                              </td>
                              <td>
                                <FaAndroid className="reports-font-style-grey" />{' '}
                                {item.visorId}
                              </td>
                              <td className="transfer-actions-cell">
                                {!item.isPendingTransfer && (
                                  <Button
                                    className="transfer-btn transfer-btn-request"
                                    buttonStyle="btn-alternate"
                                    onClick={() => openTransferModal(item)}
                                    title="Submit a transfer request for this user"
                                  >
                                    Request transfer
                                  </Button>
                                )}
                                {item.isPendingTransfer && (
                                  <Button
                                    className="transfer-btn transfer-btn-cancel"
                                    buttonStyle="btn-warn"
                                    onClick={() =>
                                      handleCancelTransfer(item, false)
                                    }
                                    title="Cancel an existing transfer request for this user"
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}

                        {activeTab === 'transfers' &&
                          sortedTransfers.map((item) => (
                            <tr
                              key={item.id}
                              className="transfer-requester-highlight-row"
                              style={getRequesterHighlightStyle(item.requester)}
                            >
                              <td>
                                <FaSitemap className="reports-font-style-grey" />{' '}
                                {item.groupName || '—'}
                              </td>
                              <td>
                                <FaAndroid className="reports-font-style-grey" />{' '}
                                {item.visorId}
                              </td>
                              <td>{item.requester}</td>
                              <td>{item.description || '—'}</td>
                              <td>{item.requestDate}</td>
                              <td className="transfer-actions-cell">
                                <Button
                                  className="transfer-btn transfer-btn-cancel"
                                  buttonStyle="btn-warn"
                                  onClick={() =>
                                    handleCancelTransfer(item, true)
                                  }
                                  title="Cancel this transfer request"
                                >
                                  Cancel
                                </Button>
                              </td>
                            </tr>
                          ))}

                        {activeTab === 'transfers' &&
                          !isLoading &&
                          !error &&
                          sortedTransfers.length === 0 && (
                            <tr>
                              <td colSpan={6} style={{ textAlign: 'center', padding: '1rem' }}>
      {visorSearch.trim()
        ? 'No pending transfers match that visor ID.'
        : 'No pending transfer requests for this site.'}
    </td>
                            </tr>
                          )}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>

            {/* Pagination only for devices */}
            {activeTab === 'devices' && !isLoading && !error && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalRecords={sortedDeviceUsers.length}
                docked
              />
            )}
          </div>

          {transferModal.open && (
            <TransferRequestModal
              isOpen={transferModal.open}
              user={transferModal.user}
              onClose={closeTransferModal}
              onSubmit={handleSubmitTransfer}
            />
          )}
      </div>
    </div>
  );
};

export default TransfersUsersTable;
